import { useEffect, useRef, useState } from "react"

/**
 * Debounce a value that changes while the user types.
 *
 * Returns the settled value plus whether a newer value is still waiting for the
 * timer, so callers can tell "this is what the request was made with" apart from
 * "the user has moved on since". Values are compared through `serialize` rather
 * than by reference, so objects that are rebuilt on every render (form state,
 * query keys) only restart the timer when their contents actually change.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  serialize: (value: T) => string = (input) => JSON.stringify(input)
): [T, boolean] {
  const [debounced, setDebounced] = useState(value)
  const latest = useRef(value)
  latest.current = value
  const key = serialize(value)
  const isPending = key !== serialize(debounced)
  useEffect(() => {
    if (!isPending) return
    const timer = setTimeout(() => setDebounced(latest.current), delayMs)
    return () => clearTimeout(timer)
  }, [isPending, key, delayMs])
  return [debounced, isPending]
}
